import { useState } from 'react';
import { Box, TextField, Typography, Autocomplete, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import type { UserProfile } from '../../types';

const TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
];

interface ProfileFormProps {
  profile: UserProfile;
  onChange: (profile: UserProfile) => void;
  disabled?: boolean;
}

export function ProfileForm({ profile, onChange, disabled }: ProfileFormProps) {
  const [showWorkAddress, setShowWorkAddress] = useState(false);

  const handleChange = (field: keyof UserProfile, value: string | null) => {
    onChange({ ...profile, [field]: value || null });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Profile Information
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField
          label="First Name"
          value={profile.first_name || ''}
          onChange={(e) => handleChange('first_name', e.target.value)}
          disabled={disabled}
          fullWidth
        />
        <TextField
          label="Last Name"
          value={profile.last_name || ''}
          onChange={(e) => handleChange('last_name', e.target.value)}
          disabled={disabled}
          fullWidth
        />
      </Box>

      <TextField
        label="Phone Number"
        value={profile.phone_number || ''}
        onChange={(e) => handleChange('phone_number', e.target.value)}
        disabled={disabled}
        fullWidth
        margin="normal"
        helperText="Digits only"
      />

      <Autocomplete
        options={TIMEZONE_OPTIONS}
        value={profile.timezone_city || null}
        onChange={(_, value) => handleChange('timezone_city', value)}
        disabled={disabled}
        freeSolo
        renderInput={(params) => (
          <TextField {...params} label="Timezone" margin="normal" />
        )}
      />

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
        Home Address
      </Typography>
      <TextField
        label="Street Address"
        value={profile.home_street_1 || ''}
        onChange={(e) => handleChange('home_street_1', e.target.value)}
        disabled={disabled}
        fullWidth
        margin="dense"
      />
      <TextField
        label="Street Address 2"
        value={profile.home_street_2 || ''}
        onChange={(e) => handleChange('home_street_2', e.target.value)}
        disabled={disabled}
        fullWidth
        margin="dense"
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 2, mt: 1 }}>
        <TextField
          label="City"
          value={profile.home_city || ''}
          onChange={(e) => handleChange('home_city', e.target.value)}
          disabled={disabled}
          fullWidth
        />
        <TextField
          label="State"
          value={profile.home_state || ''}
          onChange={(e) => handleChange('home_state', e.target.value)}
          disabled={disabled}
          fullWidth
        />
        <TextField
          label="ZIP"
          value={profile.home_zip || ''}
          onChange={(e) => handleChange('home_zip', e.target.value)}
          disabled={disabled}
          fullWidth
        />
      </Box>
      <TextField
        label="Country"
        value={profile.home_country || ''}
        onChange={(e) => handleChange('home_country', e.target.value)}
        disabled={disabled}
        fullWidth
        margin="normal"
      />

      <Box
        onClick={() => setShowWorkAddress(!showWorkAddress)}
        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mt: 3, mb: 1 }}
      >
        <Typography variant="subtitle1">Work Address</Typography>
        {showWorkAddress ? <ExpandLess /> : <ExpandMore />}
      </Box>
      <Collapse in={showWorkAddress}>
        <TextField
          label="Street Address"
          value={profile.work_street_1 || ''}
          onChange={(e) => handleChange('work_street_1', e.target.value)}
          disabled={disabled}
          fullWidth
          margin="dense"
        />
        <TextField
          label="Street Address 2"
          value={profile.work_street_2 || ''}
          onChange={(e) => handleChange('work_street_2', e.target.value)}
          disabled={disabled}
          fullWidth
          margin="dense"
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 2, mt: 1 }}>
          <TextField
            label="City"
            value={profile.work_city || ''}
            onChange={(e) => handleChange('work_city', e.target.value)}
            disabled={disabled}
            fullWidth
          />
          <TextField
            label="State"
            value={profile.work_state || ''}
            onChange={(e) => handleChange('work_state', e.target.value)}
            disabled={disabled}
            fullWidth
          />
          <TextField
            label="ZIP"
            value={profile.work_zip || ''}
            onChange={(e) => handleChange('work_zip', e.target.value)}
            disabled={disabled}
            fullWidth
          />
        </Box>
      </Collapse>
    </Box>
  );
}
